import { type FieldMetadata, useInputControl } from '@conform-to/react';
import { type ReactNode } from 'react';

import { Label, type LabelProps } from '~/components/ui/label.tsx';
import { Select, type SelectProps, SelectItem } from '~/components/ui/select.tsx';
import { cn } from '~/styles/extended-theme.ts';

import { FieldErrors } from './form-errors.tsx';

export interface FormSelectProps {
  field: FieldMetadata<string>;
  label?: string;
  withAsterisk?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  selectProps?: Omit<
    SelectProps,
    | 'id'
    | 'name'
    | 'aria-invalid'
    | 'aria-describedby'
    | 'placeholder'
    | 'defaultValue'
    | 'disabled'
    | 'children'
  >;
  labelProps?: Omit<LabelProps, 'htmlFor'>;
  onChange?: (value: string) => void;
}

function SelectField({
  field,
  label,
  placeholder,
  withAsterisk,
  disabled,
  className,
  labelProps,
  selectProps,
  children,
  onChange,
}: FormSelectProps) {
  const control = useInputControl(field);

  const handleValueChange = (value: string) => {
    control.change(value);
    onChange?.(value);
  };

  return (
    <div className={cn('grid w-full items-center gap-2 pb-2', className)}>
      {label ? (
        <Label
          className={cn('font-bold', disabled && 'opacity-50')}
          htmlFor={field.id}
          {...labelProps}
        >
          {label} {withAsterisk ? <span>*</span> : null}
        </Label>
      ) : null}
      <input
        name={field.name}
        className="hidden"
        tabIndex={-1}
        value={control.value ?? ''}
        onChange={() => {
          /* handled by select */
        }}
      />
      <Select
        {...selectProps}
        value={control.value}
        onValueChange={handleValueChange}
        id={field.id}
        placeholder={placeholder}
        disabled={disabled}
      >
        {children}
      </Select>
      <div className="px-4 pb-3 pt-1">
        {field.errorId ? <FieldErrors id={field.errorId} errors={field.errors} /> : null}
      </div>
    </div>
  );
}

export { SelectField, SelectItem };
